import { FormulaEvaluator } from "./evaluator";
import { isMissing } from "./type-registry";
import { SCALAR_UNIT, TIMESTAMP_UNIT_SECONDS } from "../unit-system";
/** An EvalError carries `message` and no `value`; an EvalValue always has `value`. */
function isEvalError(v) {
    return !("value" in v);
}
/**
 * Evaluate an AST node against a variable scope and normalize the result for the
 * editor. Pure — no I/O. Returns `missing` (with the blank references that caused
 * it, best-effort) when an input is empty, and `error` with the engine message
 * when evaluation fails.
 */
export function evaluateNode(node, scope) {
    // Most failures come back as an EvalError, but a function's execute() can
    // throw (e.g. degenerate inputs) — never let that escape, or the editor's
    // inlay/hover that call this would break.
    let result;
    try {
        result = new FormulaEvaluator(scope).evaluate(node);
    }
    catch (err) {
        return { kind: "error", message: err instanceof Error ? err.message : String(err) };
    }
    if (isEvalError(result))
        return { kind: "error", message: result.message };
    if (isMissing(result))
        return { kind: "missing", blankRefs: findBlankRefs(node, scope) };
    switch (result.type) {
        case "scalar": {
            const unit = result.unit;
            if (unit === TIMESTAMP_UNIT_SECONDS)
                return { kind: "time", value: result.value };
            return {
                kind: "number",
                value: result.value,
                unit: unit && unit !== SCALAR_UNIT ? unit : undefined,
            };
        }
        case "boolean":
            return { kind: "boolean", value: Boolean(result.value) };
        case "array":
            return { kind: "list", length: Array.isArray(result.value) ? result.value.length : 0 };
        case "object":
            return { kind: "row" };
        case "table":
            return { kind: "table" };
        default:
            return { kind: "error", message: `Unsupported result type "${result.type}".` };
    }
}
/**
 * Bare identifier references in a subtree that resolve to a blank/absent value —
 * so the hover can name *what* is blank ("no value — wet_weight is blank").
 * Best-effort: only plain `VariableAccess` paths (locals, `upstream.x`, `row.x`,
 * `sample.test.attr`), not values read off function results.
 */
function findBlankRefs(node, scope) {
    const evaluator = new FormulaEvaluator(scope);
    const blanks = new Set();
    const visit = (n) => {
        if (n.type === "VariableAccess") {
            const value = evaluator.evaluate(n);
            if (!isEvalError(value) && isMissing(value)) {
                blanks.add([n.identifier, ...n.properties].join("."));
            }
            return;
        }
        switch (n.type) {
            case "FunctionCall":
                n.arguments.forEach(visit);
                break;
            case "MemberAccess":
                visit(n.object);
                break;
            case "BinaryOperation":
            case "Comparison":
            case "Logical":
                visit(n.left);
                visit(n.right);
                break;
            case "UnaryOperation":
            case "Not":
                visit(n.operand);
                break;
        }
    };
    visit(node);
    return [...blanks];
}
//# sourceMappingURL=evaluate-node.js.map